// qualifications.js — BL-017 (Opportunity Qualification Engine)
//
// Authenticated data-access layer for the qualification API. Same JWT-Bearer
// pattern as lib/leads: pull the live access token from the Supabase session
// and call the Netlify functions, which enforce user_id from the verified
// token. All helpers return the shared { data, error } shape and never throw.

import { supabase } from './supabaseClient';
import { API_BASE_URL } from '../services/api';

const NOT_SIGNED_IN = {
  data: null,
  error: { message: 'Not signed in. Please sign in and try again.' },
};

const NOT_CONFIGURED = {
  data: null,
  error: {
    message:
      'Supabase is not configured. Fill in frontend/.env (REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY) and restart the dev server.',
  },
};

async function callApi(path, init = {}) {
  if (!supabase) return NOT_CONFIGURED;

  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData?.session?.access_token;
  if (!accessToken) return NOT_SIGNED_IN;

  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
        ...(init.headers || {}),
      },
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok || payload?.success === false) {
      return {
        data: null,
        error: {
          message: payload?.error || `Request failed (HTTP ${response.status}).`,
          status: response.status,
          payload,
        },
      };
    }
    return { data: payload, error: null };
  } catch (err) {
    return { data: null, error: { message: err?.message || 'Network error.' } };
  }
}

/**
 * Run an opportunity triage. `input` carries lat/lng (required), optional
 * address/city/state/zip, asset_type, asking_price, and the four self-declared
 * regulatory answers. Returns { data: { qualification, recommendation, ... }, error }.
 */
export async function qualifyOpportunity(input) {
  return callApi('/qualify-opportunity', {
    method: 'POST',
    body: JSON.stringify(input || {}),
  });
}

/**
 * Record post-verdict outcome signals on a qualification the user owns.
 * Only the supplied fields are sent (overridden / saved_as_lead / lead_id /
 * analysis_id). Returns { data: { qualification }, error }.
 */
export async function updateQualification({ id, overridden, saved_as_lead, lead_id, analysis_id }) {
  const body = { id };
  if (overridden !== undefined) body.overridden = overridden;
  if (saved_as_lead !== undefined) body.saved_as_lead = saved_as_lead;
  if (lead_id !== undefined) body.lead_id = lead_id;
  if (analysis_id !== undefined) body.analysis_id = analysis_id;
  return callApi('/update-qualification', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}
